import { ref, watch, type Ref } from 'vue'
import { defineStore, type StoreGeneric } from 'pinia'

import type { ProfileBaseInfo, ProfileWallet } from '@/modules/profile/types/profile'
import { loadProfileBaseInfo } from '@/modules/profile/services/profileService'
import { ProfileApi } from '@/modules/profile/api/profileApi'
import { requiresSponsor } from '@/modules/profile/services/sponsorAccess'
import { httpClient } from '@/shared/api/httpClient'
import { assertCurrentAuthRequest } from '@/shared/api/authRequestGuard'
import { createTokenStorage } from '@space/shared-api-client'
import { isNetworkError } from '@/shared/utils/networkErrorDetection'
import { i18n } from '@/i18n'
import {
  canRedirectToTmaInit,
  tmaAddBreadcrumb,
  getAuthStateContext,
} from '@/mini-app/tmaDebugLogger'

const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken'
const tokenStorage = createTokenStorage({ storageKey })

interface ProfileState {
  sponsorPromptOpen: boolean
  profile: ProfileBaseInfo | null
  loading: boolean
  error: string | null
}

interface LoginSuccessPayload {
  success: number
  location: string
  user_id: number
  // 2FA fields
  requires_approval?: boolean
  approval_type?: 'otp' | 'totp'
  approval_methods?: string[]
  session_id?: number
  // Спринт 14: два канала — код не отправлен, бэкенд ждёт ответа «куда прислать».
  choose?: { channels: string[] }
  // SSO field
  sso_redirect?: string
}

interface LoginSuccessResponse {
  result: 'success'
  data: LoginSuccessPayload
}

interface LoginErrorResponse {
  result: 'error'
  error_code?: number
  error_description?: string
  error_details?: string
  error_message?: string
}

type LoginResponse = LoginSuccessResponse | LoginErrorResponse

// Незавершённый вход уже дал токен, но защищённый профиль ещё недоступен.
const requiresLoginApproval = (data: LoginSuccessPayload) =>
  data.requires_approval || !!data.choose || (data.location ?? '').includes('login-approval')

interface ApprovalCallbacks {
  onPendingToken?: (token: string) => void
  onApproved?: () => void
}

type LoginPayload = Record<string, string | number | boolean>

interface LoginApprovalPayload {
  code: string
  redirect?: string
  referer?: string
}

// OTP-R3: lifetime blocks guarded auth admission even when an action releases
// busy for a nested profile refresh. Guarded auth/default profile work is supersedable.
interface ProfileOwnership {
  current?: () => boolean
  currentBusy: boolean
  foreign: Set<object>
  busy: Set<object>
  revision: Ref<number>
}
const profileOwners = new WeakMap<object, ProfileOwnership>()
const ownership = (store: StoreGeneric): ProfileOwnership => {
  let state = profileOwners.get(store)
  if (!state) {
    state = { currentBusy: false, foreign: new Set(), busy: new Set(), revision: ref(0) }
    profileOwners.set(store, state)
  }
  return state
}
const syncBusy = (store: StoreGeneric) => {
  const owned = ownership(store)
  store.loading = owned.currentBusy || owned.busy.size > 0
}
const beginForeign = (store: StoreGeneric) => {
  const owned = ownership(store)
  const handle = {}
  owned.current = undefined
  owned.currentBusy = false
  owned.foreign.add(handle)
  owned.busy.add(handle)
  syncBusy(store)
  owned.revision.value++
  return {
    releaseBusy() {
      owned.busy.delete(handle)
      syncBusy(store)
    },
    end() {
      owned.busy.delete(handle)
      owned.foreign.delete(handle)
      syncBusy(store)
      owned.revision.value++
    },
  }
}
const waitForForeign = (owned: ProfileOwnership, source: () => boolean) =>
  new Promise<void>((resolve) => {
    const stop = watch(() => [source(), owned.revision.value], () => {
      if (!source() || !owned.foreign.size) {
        stop()
        resolve()
      }
    }, { flush: 'sync' })
    if (!source() || !owned.foreign.size) { stop(); resolve() }
  })

// Shared admission/cancellation lifecycle for guarded auth operations.
// Without source the existing default ownership path is preserved.
const runAuthOperation = async <T>(store: StoreGeneric, source: (() => boolean) | undefined, run: (current?: () => boolean) => Promise<T>): Promise<T> => {
  if (source) assertCurrentAuthRequest(source)
  const owned = ownership(store)
  if (source) {
    while (owned.foreign.size) {
      await waitForForeign(owned, source)
      assertCurrentAuthRequest(source)
    }
    assertCurrentAuthRequest(source)
  }
  // No await between the last blocker check and ownership claim.
  const foreign = source ? undefined : beginForeign(store)
  const current = () => owned.current === current && (!source || source())
  owned.current = current
  const stopCancellation = source ? watch(source, (active) => {
    if (!active && owned.current === current) {
      owned.current = undefined
      owned.currentBusy = false
      syncBusy(store)
    }
    if (!active) stopCancellation?.()
  }, { flush: 'sync' }) : undefined
  owned.currentBusy = true
  syncBusy(store)
  store.error = null
  try {
    return await run(source ? current : undefined)
  } finally {
    stopCancellation?.()
    if (owned.current === current) {
      owned.currentBusy = false
      owned.current = undefined
      syncBusy(store)
    }
    foreign?.end()
  }
}

export const useProfileStore = defineStore('profile', {
  state: (): ProfileState => ({
    sponsorPromptOpen: true,
    profile: null,
    loading: false,
    error: null,
  }),
  getters: {
    needsSponsor(state): boolean {
      return requiresSponsor(state.profile)
    },
    fullName(state): string {
      if (!state.profile) {
        return ''
      }

      return `${state.profile.first_name} ${state.profile.last_name}`.trim()
    },
    primaryWallet(state): ProfileWallet | null {
      return state.profile?.wallet?.[0] ?? null
    },
    walletBalanceLabel(): string {
      return this.primaryWallet?.amount_string ?? '0'
    },
    avatarUrl(state): string | null {
      // Return state profile photo first (reactive), fallback to localStorage for immediate display
      return state.profile?.photo ?? localStorage.getItem('user_avatar') ?? null
    },
    isAmbassador(state): boolean {
      if (state.profile?.career?.is_ambassador !== undefined) {
        return state.profile.career.is_ambassador === true
      }
      // Fallback to cached value before base-info loads
      return localStorage.getItem('is_ambassador') === 'true'
    },
    referralLink(state): string {
      return state.profile?.reflink ?? state.profile?.referral_link ?? state.profile?.referal_link ?? ''
    },
    emailAction(state): 'attach' | 'verify' | 'change' {
      const email = state.profile?.email?.trim() ?? ''
      if (!email || /@telegram\.org$/i.test(email)) return 'attach'
      return state.profile?.verify_email === 1 ? 'change' : 'verify'
    },
    hasCountry(state): boolean {
      return !!state.profile?.country?.code
    },
  },
  actions: {
    async assignSponsor(login: string): Promise<boolean> {
      await ProfileApi.assignSponsor(login.trim())
      // Do not optimistically unlock: only a fresh base-info can grant access.
      await this.fetchProfile(true)
      return !!this.profile && !requiresSponsor(this.profile) && Number(this.profile.referal) > 0
    },
    async fetchProfile(force = false, isCurrent?: () => boolean) {
      if (isCurrent) assertCurrentAuthRequest(isCurrent)
      const owned = ownership(this)
      // Only the continuation of the actual busy owner may bypass loading.
      if (this.loading && (!isCurrent || owned.current !== isCurrent)) {
        return
      }

      if (!force && this.profile) {
        return
      }

      const current: () => boolean = isCurrent ?? (() => owned.current === current)
      owned.current = current
      owned.currentBusy = true
      syncBusy(this)
      this.error = null

      try {
        const profile = await loadProfileBaseInfo(current)
        assertCurrentAuthRequest(current)
        this.profile = profile

        // Cache avatar URL
        if (this.profile?.photo) {
          localStorage.setItem('user_avatar', this.profile.photo)
        } else {
          // Should we remove it if it's null? Maybe, but let's prevent flickering first. 
          // If API returns null, we should probably respect that eventually.
          if (this.profile && this.profile.photo === null) {
            localStorage.removeItem('user_avatar')
          }
        }

        // Cache ambassador status
        const isAmbassador = this.profile?.career?.is_ambassador
        if (isAmbassador !== undefined) {
          localStorage.setItem('is_ambassador', String(isAmbassador))
        }

      } catch (error: any) {
        if (!current()) {
          if (isCurrent) assertCurrentAuthRequest(current)
          return
        }
        // TMA mode: if base-info returns error_code 100 (not authenticated),
        // redirect to /tma-init for re-authentication
        const errorCode = error?.errorCode ?? error?.response?.data?.error_code
        const isTmaUser = document.cookie.split('; ').some(c => c === 'tma_user=1')
        if (errorCode === 100 && isTmaUser) {
          // --- Debug: log re-auth attempt with full context ---
          tmaAddBreadcrumb('profileStore: error_code 100 + tma_user cookie — re-auth triggered', {
            errorCode,
            ...getAuthStateContext(),
            error_message: error?.message ?? error?.response?.data?.error_description ?? null,
          }, 'warning')

          // --- Loop protection ---
          if (canRedirectToTmaInit()) {
            console.warn('[GonkaGame][profile] TMA auth expired (error_code: 100) — redirecting to /tma-init')
            this.error = 'tma_auth_expired'
            this.profile = null
            window.location.href = '/tma-init'
          } else {
            // Loop detected — DO NOT redirect, show error instead
            console.error('[GonkaGame][profile] TMA re-auth loop detected — NOT redirecting')
            this.error = 'tma_auth_loop'
            this.profile = null
            // Emit event so UI can show a meaningful error
            window.dispatchEvent(new CustomEvent('gonkagame:connection-error'))
          }
          return
        }

        if (isNetworkError(error)) {
          // Network error: preserve last known profile state and show modal
          console.warn('[GonkaGame][profile] Network error during profile load - preserving state', error)
          this.error = 'network_error'
          // Emit event for connection error modal
          window.dispatchEvent(new CustomEvent('gonkagame:connection-error'))
        } else {
          // Check if this is a 401 Unauthorized response - only then should we clear profile
          const status = error?.response?.status ?? error?.status
          if (status === 401) {
            console.warn('[GonkaGame][profile] Unauthorized (401) - clearing profile', error)
            this.error = 'unauthorized'
            this.profile = null
          } else {
            // Server error (5xx) or other: preserve last known profile state
            console.warn('[GonkaGame][profile] Server error during profile load - preserving state', error)
            this.error = 'server_error'
            // Emit event for connection error modal - same behavior as network errors
            window.dispatchEvent(new CustomEvent('gonkagame:connection-error'))
          }
        }
      } finally {
        if (owned.current === current) {
          owned.currentBusy = false
          syncBusy(this)
          if (!isCurrent) owned.current = undefined
        }
      }
    },

    async updateCountry(countryCode: string): Promise<void> {
      const response = await httpClient.post<{ result: string; country?: { code: string; name: string } }>(
        '/profile/update-country',
        { country_code: countryCode }
      )

      if (response.result === 'success' && response.country && this.profile) {
        this.profile.country = response.country
      }
    },

    async login(payload: LoginPayload, isCurrent?: () => boolean, onPendingToken?: (token: string) => void): Promise<LoginSuccessPayload> {
      return runAuthOperation(this, isCurrent, async (current) => {
        try {
          const data = current
            ? await httpClient.guardedRequest<LoginResponse>('/profile/login', current, { method: 'POST', body: JSON.stringify(payload) },
              ({ token, ok, data }) => {
                const response = data as LoginResponse
                if (ok && token && response.result === 'success' && response.data?.success && requiresLoginApproval(response.data)) onPendingToken?.(token)
              })
            : await httpClient.post<LoginResponse>('/profile/login', payload)
          if (current) assertCurrentAuthRequest(current)

          if (data.result === 'success' && data.data?.success) {
            const storedToken = httpClient.getToken()
            if (storedToken) {
              tokenStorage.setToken(storedToken)
            }

            if (!requiresLoginApproval(data.data)) {
              if (current) {
                await this.fetchProfile(true, current)
                assertCurrentAuthRequest(current)
              } else {
                await this.fetchProfile(true)
              }
            }

            return data.data
          }

          const errorResponse = data as LoginErrorResponse
          const apiError = errorResponse.error_message ?? errorResponse.error_details ?? errorResponse.error_description ?? i18n.global.t('errors.loginFailed')
          this.error = apiError
          throw new Error(apiError)
        } catch (error: any) {
          if (current) assertCurrentAuthRequest(current)
          // Preserve original error message for UI
          const message = error?.message ?? i18n.global.t('errors.loginFailed')
          this.error = message
          // Rethrow original error to preserve response.data for downstream handlers
          throw error
        }
      })
    },

    async confirmLoginApproval(payload: LoginApprovalPayload, isCurrent?: () => boolean, callbacks?: ApprovalCallbacks): Promise<LoginSuccessPayload> {
      const run = async (current?: () => boolean): Promise<LoginSuccessPayload> => {
        try {
          const data = current
            ? await httpClient.guardedRequest<LoginResponse>('/profile/login-approval', current, { method: 'POST', body: JSON.stringify(payload) },
              ({ token, ok, data }) => {
                const response = data as LoginResponse
                if (ok && response.result === 'success' && response.data?.success) callbacks?.onApproved?.()
                else if (token) callbacks?.onPendingToken?.(token)
              })
            : await httpClient.post<LoginResponse>('/profile/login-approval', payload)
          if (current) assertCurrentAuthRequest(current)

          if (data.result === 'success' && data.data?.success) {
            const storedToken = httpClient.getToken()
            if (storedToken) {
              tokenStorage.setToken(storedToken)
            }

            if (current) {
              await this.fetchProfile(true, current)
              assertCurrentAuthRequest(current)
            } else {
              await this.fetchProfile(true)
            }
            return data.data
          }

          const errorResponse = data as LoginErrorResponse
          const apiError = errorResponse.error_message ?? errorResponse.error_details ?? errorResponse.error_description ?? i18n.global.t('errors.loginConfirmFailed')
          this.error = apiError
          throw new Error(apiError)
        } catch (error: any) {
          if (current) assertCurrentAuthRequest(current)
          const message = error?.message ?? i18n.global.t('errors.loginConfirmFailed')
          this.error = message
          // R6c: error_code нужен окну входа (31 — лимит кодов из приложения).
          throw Object.assign(new Error(message), { error_code: error?.response?.data?.error_code })
        }
      }
      if (isCurrent) return runAuthOperation(this, isCurrent, run)
      const operation = beginForeign(this)
      this.error = null
      try {
        return await run()
      } finally {
        operation.end()
      }
    },

    async register(payload: Record<string, unknown>, isCurrent?: () => boolean): Promise<{ success: number; redirect?: string; user_id?: number } | undefined> {
      const run = async (current?: () => boolean) => {
        try {
          // CRITICAL: Validate password is present before sending request
          if (!payload.password || (typeof payload.password === 'string' && payload.password.trim() === '')) {
            console.error('[GonkaGame][register] BLOCKED: Attempted signup without password!', {
              email: payload.email,
              hasPassword: !!payload.password,
              passwordLength: typeof payload.password === 'string' ? payload.password.length : 0
            })
            throw new Error('Password is required for registration')
          }

          type SignupResponse = {
            result?: string
            data?: { success: number; redirect?: string; user_id?: number }
          }
          const response = current
            ? await httpClient.guardedRequest<SignupResponse>('/profile/signup', current, { method: 'POST', body: JSON.stringify(payload) })
            : await httpClient.post<SignupResponse>('/profile/signup', payload)
          if (current) assertCurrentAuthRequest(current)

          // Check for success in response
          // Token is automatically extracted from x-token header by httpClient
          if (response.data?.success === 1 || response.result === 'success') {
            // Explicitly save the auth token (same as login())
            const storedToken = httpClient.getToken()
            if (storedToken) {
              tokenStorage.setToken(storedToken)
            }

            if (current) {
              await this.fetchProfile(true, current)
              assertCurrentAuthRequest(current)
            } else {
              // Reset loading before fetchProfile to prevent early return
              operation.releaseBusy()
              // Fetch profile to fully initialize the user session
              await this.fetchProfile(true)
            }
            return response.data
          } else {
            // If we got a response but it's not success, throw the response for error parsing
            throw response
          }
        } catch (error: any) {
          if (current) assertCurrentAuthRequest(current)
          this.error = error.response?.data?.message || i18n.global.t('errors.registrationFailed')
          throw error
        }
      }
      if (isCurrent) return runAuthOperation(this, isCurrent, run)
      const operation = beginForeign(this)
      this.error = null
      try {
        return await run()
      } finally {
        operation.end()
      }
    },

    async searchSponsor(payload: Record<string, unknown>) {
      try {
        return await httpClient.post('/profile/search-sponsor', payload)
      } catch (error) {
        console.error('Sponsor search failed', error)
        throw error
      }
    },

    async updateContacts(payload: Parameters<typeof ProfileApi.updateContacts>[0]) {
      const operation = beginForeign(this)
      this.error = null
      try {
        await ProfileApi.updateContacts(payload)
        await this.fetchProfile(true) // Refresh profile data
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.contactsUpdateFailed')
        throw error
      } finally {
        operation.end()
      }
    },

    async initSession(actionName: string, payload: Record<string, any> = {}) {
      const operation = beginForeign(this)
      this.error = null
      try {
        return await ProfileApi.initActionSession(actionName, payload)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.sessionInitFailed')
        throw error
      } finally {
        operation.end()
      }
    },

    async changePassword(payload: Parameters<typeof ProfileApi.changePassword>[0]) {
      const operation = beginForeign(this)
      this.error = null
      try {
        await ProfileApi.changePassword(payload)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.passwordChangeFailed')
        throw error
      } finally {
        operation.end()
      }
    },

    async changePayPassword(payload: Parameters<typeof ProfileApi.changePayPassword>[0]) {
      const operation = beginForeign(this)
      const profile = this.profile
      const token = httpClient.getToken()
      this.error = null
      try {
        const response = await ProfileApi.changePayPassword(payload)
        if (response.result === 'success' && profile?.security && this.profile === profile && httpClient.getToken() === token) {
          profile.security.pay_pass = true
        }
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.walletPasswordChangeFailed')
        throw error
      } finally {
        operation.end()
      }
    },

    async uploadAvatar(file: File) {
      const operation = beginForeign(this)
      this.error = null
      try {
        await ProfileApi.uploadAvatar(file)

        // 1. Fetch latest profile to ensure other data is synced
        await this.fetchProfile(true)

        // 2. Optimistically update with the uploaded file to prevent caching issues
        // The backend might return a cached URL or old URL for a few seconds
        const reader = new FileReader()
        reader.onload = (e) => {
          const result = e.target?.result as string
          if (result && this.profile) {
            this.profile.photo = result
            localStorage.setItem('user_avatar', result)
          }
        }
        reader.readAsDataURL(file)

      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.photoUploadFailed')
        throw error
      } finally {
        operation.end()
      }
    },

    async updatePaymentMethods(payload: Parameters<typeof ProfileApi.updatePaymentMethods>[0]) {
      const operation = beginForeign(this)
      this.error = null
      try {
        await ProfileApi.updatePaymentMethods(payload)
        await this.fetchProfile(true)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.paymentDetailsSaveFailed')
        throw error
      } finally {
        operation.end()
      }
    },

    async updateSettings(payload: Parameters<typeof ProfileApi.updateSettings>[0]) {
      const operation = beginForeign(this)
      this.error = null
      try {
        await ProfileApi.updateSettings(payload)
        await this.fetchProfile(true)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.settingsSaveFailed')
        throw error
      } finally {
        operation.end()
      }
    },

    async requestAccountDeletionCode(): Promise<void> {
      await ProfileApi.requestAccountDeletionCode()
    },

    async deleteAccount(code: string): Promise<void> {
      await ProfileApi.deleteAccount(code)
      this.clearSession()
    },

    async logout() {
      try {
        const response = await httpClient.delete<{ success: boolean; sso_redirect?: string }>('/profile/logout')

        // If SSO redirect is provided, redirect to SSO logout first
        if (response.sso_redirect) {
          window.location.href = response.sso_redirect
          return
        }
      } catch (error) {
        console.error('Logout API failed', error)
      } finally {
        this.clearSession()
      }
    },

    // Both logout and confirmed deletion must discard the same local session.
    clearSession(): void {
      tokenStorage.setToken('')
      this.profile = null
      this.sponsorPromptOpen = true
      localStorage.removeItem('user_avatar')
      localStorage.removeItem('partner_bonus_split')
      localStorage.removeItem('is_ambassador')
      localStorage.removeItem(storageKey)
      Object.keys(localStorage)
        .filter(key => key.startsWith('/api/matrix/'))
        .forEach(key => localStorage.removeItem(key))
      document.cookie = 'dasdasdaddjdj=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
      window.location.href = '/'
    },

    async sendEmailVerificationCode(email: string) {
      return await ProfileApi.sendEmailVerificationCode(email)
    },

    async sendCurrentEmailVerificationCode(isCurrent: () => boolean): Promise<void> {
      const email = this.profile?.email ?? ''
      const current = () => isCurrent() && this.profile?.email === email
      assertCurrentAuthRequest(current)
      await ProfileApi.sendLoginCode(email, { channel: 'email', purpose: 'verify_email' }, current)
      assertCurrentAuthRequest(current)
    },

    async verifyCurrentEmail(code: string, isCurrent: () => boolean): Promise<boolean> {
      const email = this.profile?.email ?? ''
      const current = () => isCurrent() && this.profile?.email === email
      assertCurrentAuthRequest(current)
      const verified = await ProfileApi.verifyEmailCode(email, code, current)
      assertCurrentAuthRequest(current)
      return verified
    },

    async changeEmail(
      payload: { email: string; code: string; confirmAction?: string; confirmActionCode?: string },
      isCurrent: () => boolean = () => true,
    ) {
      const accountId = this.profile?.id
      const current = () => isCurrent() && this.profile?.id === accountId
      assertCurrentAuthRequest(current)
      const operation = beginForeign(this)
      this.error = null
      try {
        const result = await ProfileApi.changeEmail(payload)
        assertCurrentAuthRequest(current)
        if (result.result !== 'success') throw result
        // A foreign operation holds loading=true; release it before the real refresh.
        operation.releaseBusy()
        await this.fetchProfile(true, current)
        assertCurrentAuthRequest(current)
        if (this.error) throw new Error(this.error)
      } catch (error: unknown) {
        if (current()) this.error = i18n.global.t('errors.emailChangeFailed')
        throw error
      } finally {
        operation.end()
      }
    },

    async setMfAuthorization(
      enabled: boolean,
      options?: { confirmAction?: string; confirmActionCode?: string }
    ): Promise<void> {
      const operation = beginForeign(this)
      this.error = null
      try {
        const response = await ProfileApi.setMfAuthorization(enabled, options)
        // Update local state from API response
        if (this.profile?.security) {
          this.profile.security.mf_authorization = response.enabled
        }
      } catch (error: any) {
        this.error = error.message || 'Error changing 2FA settings'
        throw error
      } finally {
        operation.end()
      }
    }
  },
})
